import React from "react";
import GoogleHoursAudit from "./GoogleHoursAudit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import { PurpleSwitch } from "../../../helpers/helpers";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import { generateTimeOptions } from "../../../helpers/helpers";
import FutureRecurringHoursGroupInput from "./FutureRecurringHoursGroupInput";
import SpecialHoursOfOperationsInput from "./SpecialHoursOfOperationsInput";
import { get_t_str } from "../../../helpers/helpers";
import { text_to_num } from "../../../helpers/helpers";
import {
  formatDate,
  formatDateReadableWithDayOfWeek,
} from "../requests/utilities/helpers";
import {
  buildFutureRecurringHoursRow,
  buildInheritedFutureRecurringHoursRow,
  getInheritedFutureRecurringHoursDefaults,
  getFutureRecurringHoursToggleOnDefaults,
  hasFutureRecurringHoursOpen,
  getNextAvailableEffectiveStartDate,
  groupFutureRecurringHours,
  normalizeFutureRecurringHours,
  normalizeHoursOfOperationData,
} from "./futureRecurringHoursHelpers";

function HoursOfOperationTableRow(props) {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const [checkingGoogle, setCheckingGoogle] = React.useState(false);
  const handleClose = () => { setOpen(false); setCheckingGoogle(false); };

  const [hoursOfOperationData, setHoursOfOperationsData] = React.useState(
    normalizeHoursOfOperationData(props.location)
  );

  const currentDate = new Date();

  React.useEffect(() => {
    setHoursOfOperationsData(normalizeHoursOfOperationData(props.location));
  }, [props.location]);

  React.useEffect(() => {
    if (open) {
      setHoursOfOperationsData(normalizeHoursOfOperationData(props.location));
    }
  }, [open, props.location]);

  const handleAddSpecialHours = () => {
    const newSpecialHours = {
      store: hoursOfOperationData.storeId,
      date: formatDate(currentDate),
      open_time: hoursOfOperationData.standardOperatingHours[0].open_time,
      close_time: hoursOfOperationData.standardOperatingHours[0].close_time,
    };
    const updatedSpecialHours = [
      ...hoursOfOperationData.specialHours,
      newSpecialHours,
    ];
    setHoursOfOperationsData({
      ...hoursOfOperationData,
      specialHours: updatedSpecialHours,
    });
  };

  const handleDeleteSpecialHours = (index) => {
    const updatedSpecialHours = [...hoursOfOperationData.specialHours];
    updatedSpecialHours.splice(index, 1);
    setHoursOfOperationsData({
      ...hoursOfOperationData,
      specialHours: updatedSpecialHours,
    });
  };

  const handleAddFutureRecurringHoursGroup = () => {
    setHoursOfOperationsData((previousData) => {
      const existingEffectiveDates = groupFutureRecurringHours(
        previousData.futureRecurringHours
      ).map((group) => group.effective_start_date);
      const effectiveStartDate = getNextAvailableEffectiveStartDate(
        existingEffectiveDates
      );
      const newRows = Array.from({ length: 7 }, (_, index) =>
        buildFutureRecurringHoursRow({
          storeId: previousData.storeId,
          effectiveStartDate,
          dow: index + 1,
          standardOperatingHours: previousData.standardOperatingHours,
        })
      );

      return {
        ...previousData,
        futureRecurringHours: normalizeFutureRecurringHours({
          futureRecurringHours: [
            ...previousData.futureRecurringHours,
            ...newRows,
          ],
          storeId: previousData.storeId,
        }),
      };
    });
  };

  const handleDeleteFutureRecurringHoursGroup = (effectiveStartDate) => {
    setHoursOfOperationsData((previousData) => ({
      ...previousData,
      futureRecurringHours: previousData.futureRecurringHours.filter(
        (row) => row.effective_start_date !== effectiveStartDate
      ),
    }));
  };

  const handleFutureRecurringHoursDateChange = (
    currentEffectiveStartDate,
    nextEffectiveStartDate
  ) => {
    setHoursOfOperationsData((previousData) => ({
      ...previousData,
      futureRecurringHours: normalizeFutureRecurringHours({
        futureRecurringHours: previousData.futureRecurringHours.map((row) =>
          row.effective_start_date === currentEffectiveStartDate
            ? {
                ...row,
                effective_start_date: nextEffectiveStartDate,
              }
            : row
        ),
        storeId: previousData.storeId,
      }),
    }));
  };

  const handleFutureRecurringHoursChange = (
    effectiveStartDate,
    dow,
    field,
    value
  ) => {
    setHoursOfOperationsData((previousData) => ({
      ...previousData,
      futureRecurringHours: (() => {
        const matchingRowIndex = previousData.futureRecurringHours.findIndex(
          (row) =>
            row.effective_start_date === effectiveStartDate && row.dow === dow
        );

        if (matchingRowIndex === -1) {
          const newRow = {
            ...buildInheritedFutureRecurringHoursRow({
              futureRecurringHours: previousData.futureRecurringHours,
              storeId: previousData.storeId,
              effectiveStartDate,
              dow,
              standardOperatingHours: previousData.standardOperatingHours,
            }),
            [field]: value,
          };

          return normalizeFutureRecurringHours({
            futureRecurringHours: [
              ...previousData.futureRecurringHours,
              newRow,
            ],
            storeId: previousData.storeId,
          });
        }

        return previousData.futureRecurringHours.map((row, index) =>
          index === matchingRowIndex
            ? {
                ...row,
                [field]: value,
              }
            : row
        );
      })(),
    }));
  };

  const handleFutureRecurringHoursToggle = (effectiveStartDate, dow) => {
    setHoursOfOperationsData((previousData) => ({
      ...previousData,
      futureRecurringHours: (() => {
        const matchingRowIndex = previousData.futureRecurringHours.findIndex(
          (row) =>
            row.effective_start_date === effectiveStartDate && row.dow === dow
        );

        if (matchingRowIndex === -1) {
          const inheritedHours = getInheritedFutureRecurringHoursDefaults({
            futureRecurringHours: previousData.futureRecurringHours,
            effectiveStartDate,
            dow,
            standardOperatingHours: previousData.standardOperatingHours,
          });
          const nextRowHours = hasFutureRecurringHoursOpen(inheritedHours)
            ? { open_time: 0, close_time: 0 }
            : getFutureRecurringHoursToggleOnDefaults({
                futureRecurringHours: previousData.futureRecurringHours,
                effectiveStartDate,
                dow,
                standardOperatingHours: previousData.standardOperatingHours,
              });

          return normalizeFutureRecurringHours({
            futureRecurringHours: [
              ...previousData.futureRecurringHours,
              {
                ...buildInheritedFutureRecurringHoursRow({
                  futureRecurringHours: previousData.futureRecurringHours,
                  storeId: previousData.storeId,
                  effectiveStartDate,
                  dow,
                  standardOperatingHours: previousData.standardOperatingHours,
                }),
                open_time: nextRowHours.open_time,
                close_time: nextRowHours.close_time,
              },
            ],
            storeId: previousData.storeId,
          });
        }

        return previousData.futureRecurringHours.map((row, index) => {
          if (index !== matchingRowIndex) {
            return row;
          }

          const isOpen = row.open_time !== 0 || row.close_time !== 0;
          if (isOpen) {
            return {
              ...row,
              open_time: 0,
              close_time: 0,
            };
          }

          const defaultHours = getFutureRecurringHoursToggleOnDefaults({
            futureRecurringHours: previousData.futureRecurringHours,
            effectiveStartDate,
            dow,
            standardOperatingHours: previousData.standardOperatingHours,
          });

          return {
            ...row,
            open_time: defaultHours.open_time,
            close_time: defaultHours.close_time,
          };
        });
      })(),
    }));
  };

  const dayOfWeekNameLookup = {
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
    7: "Sun",
  };

  const dayOfWeekFullNameLookup = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
  };

  function condenseOperatingHours(hoursArr) {
    const orderedDict = new Map();
    for (const obj of hoursArr) {
      const key =
        obj.open_time === 0 && obj.close_time === 0
          ? `Closed`
          : `${get_t_str(obj.open_time)} - ${get_t_str(obj.close_time)}`;
      if (orderedDict.has(key)) {
        orderedDict.get(key);
        orderedDict.get(key).push(obj.dow);
      } else {
        orderedDict.set(key, [obj.dow]);
      }
    }
    return orderedDict;
  }

  const condensedOperatingHours = condenseOperatingHours(
    props.location.standardOperatingHours
  );
  const futureRecurringGroups = React.useMemo(
    () => groupFutureRecurringHours(hoursOfOperationData.futureRecurringHours),
    [hoursOfOperationData.futureRecurringHours]
  );


  function findGroups(array) {
    let groups = [];
    let start = array[0];
    let end = array[0];

    for (let i = 1; i < array.length; i++) {
      if (array[i] === end + 1) {
        end = array[i];
      } else {
        groups.push(
          start === end
            ? `${dayOfWeekNameLookup[start]}, `
            : `${dayOfWeekNameLookup[start]} - ${dayOfWeekNameLookup[end]}, `
        );
        start = end = array[i];
      }
    }

    groups.push(
      start === end
        ? `${dayOfWeekNameLookup[start]}, `
        : `${dayOfWeekNameLookup[start]} - ${dayOfWeekNameLookup[end]}, `
    );

    return groups;
  }

  const handleChange = (evt, index, field) => {
    const value = evt.target.value;
    const updatedStandardOperatingHours = [
      ...hoursOfOperationData.standardOperatingHours,
    ];
    const updatedDay = {
      ...updatedStandardOperatingHours[index],
      [field]: text_to_num(value),
    };
    updatedStandardOperatingHours[index] = updatedDay;
    setHoursOfOperationsData({
      ...hoursOfOperationData,
      standardOperatingHours: updatedStandardOperatingHours,
    });
  };

  // Function to check if the store has operating hours for a specific day
  const hasOperatingHoursForDay = (day) =>
    day.open_time !== 0 || day.close_time !== 0;

  // Function to get the checked state for a specific day
  const getCheckedStateForDay = (day) => {
    return hasOperatingHoursForDay(day);
  };

  // Function to handle the toggle of PurpleSwitch
  const handlePurpleSwitchToggle = (dayIndex) => {
    const updatedStandardOperatingHours = [
      ...hoursOfOperationData.standardOperatingHours,
    ];
    const currentDay = updatedStandardOperatingHours[dayIndex];

    // If PurpleSwitch is checked, set open_time and close_time to 0
    if (hasOperatingHoursForDay(currentDay)) {
      updatedStandardOperatingHours[dayIndex] = {
        ...currentDay,
        open_time: 0,
        close_time: 0,
      };
    } else {
      updatedStandardOperatingHours[dayIndex] = {
        ...currentDay,
        open_time: props.location.standardOperatingHours[0].open_time
          ? props.location.standardOperatingHours[0].open_time
          : 9,
        close_time: props.location.standardOperatingHours[0].close_time
          ? props.location.standardOperatingHours[0].close_time
          : 17,
      };
    }

    setHoursOfOperationsData({
      ...hoursOfOperationData,
      standardOperatingHours: updatedStandardOperatingHours,
    });
  };

  async function handleSubmit() {
    const didSave = await props.update_hours_for_all_stores(hoursOfOperationData);
    if (didSave) {
      handleClose();
    }
  }

  const maxStartQhnum = props.storeNames.reduce((max, obj) => {
    return obj.start_qhnum > max ? obj.start_qhnum : max;
  }, -Infinity);

  return (
    <>
      {props.embedded ? (
        <div id={`hours-store-${props.location.storeId}`} className="rounded-lg bg-slate-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="mb-2 text-xs font-medium text-slate-500">Standard hours</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                {Array.from(condensedOperatingHours.entries()).map(([time, days]) => (
                  <React.Fragment key={time}>
                    <dt className="text-slate-600">{findGroups(days)}</dt>
                    <dd className="font-medium text-slate-900">{time}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
            <button type="button" onClick={handleOpen}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2">
              <EditIcon sx={{ fontSize: 16 }} />
              Edit hours
            </button>
          </div>
          {props.location.specialHours.length > 0 ? (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-slate-500">Special hours / overrides</p>
              <dl className="space-y-2 text-sm">
                {props.location.specialHours.map((day, index) => (
                  <div key={index} className="flex flex-wrap gap-x-4 gap-y-1">
                    <dt className="text-slate-600">{formatDateReadableWithDayOfWeek(day.date)}</dt>
                    <dd className="font-medium text-slate-900">
                      {day.open_time !== 0 && day.close_time !== 0
                        ? `${get_t_str(day.open_time)} - ${get_t_str(day.close_time)}`
                        : "Closed"}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : <p className="mt-3 text-xs text-slate-500">No special hours upcoming</p>}
        </div>
      ) : <tr
        key={props.i}
        id={`hours-store-${props.location.storeId}`}
        className="hover:bg-slate-50 cursor-pointer"
        onClick={handleOpen}
      >
        <td className="whitespace-nowrap py-5 pl-6 pr-3 text-sm">
          <div>{props.location.locationName}</div>
        </td>
        <td className="whitespace-nowrap px-3 py-5 text-sm text-slate-900">
          <div className="w-full">
            {Array.from(condensedOperatingHours.entries()).map(
              ([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <p className="text-slate-900">{findGroups(value)}</p>
                  <p className="text-violet-700">{key}</p>
                </div>
              )
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-5 text-sm text-slate-900">
          {props.location.specialHours.length > 0 ? (
            <div className="w-full">
              {props.location.specialHours.map((day, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <p>{formatDateReadableWithDayOfWeek(day.date)}</p>
                  <p className="text-violet-700">
                    {day.open_time !== 0 && day.close_time !== 0
                      ? `${get_t_str(day.open_time)} - ${get_t_str(
                          day.close_time
                        )}`
                      : "Closed"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-slate-500">No special hours upcoming</div>
          )}
        </td>
        <td className="relative whitespace-nowrap py-5 pl-12 pr-3 text-right text-sm font-medium">
          <button type="button" aria-label={`Edit operating hours for ${props.location.locationName}`}
            onClick={(event) => { event.stopPropagation(); handleOpen(); }}
            className="rounded p-2 text-slate-500 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
            <ChevronRightIcon />
          </button>
        </td>
      </tr>}
      <Drawer open={open} anchor="right" onClose={handleClose}>
        <div className="w-[450px] max-w-[100vw] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={props.location.locationName}
              onClose={handleClose}
            />
            <div className="w-full px-6">
              <div className="mt-5">
                <p className="text-slate-900 text-sm font-semibold">
                  Set standard hours
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Configure the standard hours of operation for this location
                </p>
              </div>
              {props.canCheckPublishedHours && <div className="mt-3">
                <button type="button" onClick={() => setCheckingGoogle(value => !value)}
                  aria-expanded={checkingGoogle}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50">
                  {checkingGoogle ? "Close Google check" : "Check hours on Google"}
                </button>
                {checkingGoogle && <div className="mt-3">
                  <GoogleHoursAudit locations={[props.location]} onSaved={async () => {
                    await props.onHoursVerified?.();
                    handleClose();
                  }} />
                </div>}
              </div>}
              {/* <div className="mt-5">
                <Divider />
              </div> */}
              <div className="mt-6">
                {hoursOfOperationData.standardOperatingHours.map(
                  (day, index) => {
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between mt-4"
                      >
                        <div className="flex items-center">
                          <div>
                            <PurpleSwitch
                              size="small"
                              checked={getCheckedStateForDay(day)}
                              onChange={() => handlePurpleSwitchToggle(index)}
                            />
                          </div>
                          <p className="text-slate-900 text-xs ml-1">
                            {dayOfWeekFullNameLookup[day.dow]}
                          </p>
                        </div>
                        {day.open_time === 0 && day.close_time === 0 ? (
                          <div className="h-[34px] flex items-cente">
                            <p className="text-slate-900">Closed</p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-x-1">
                            <div className="w-[100px]">
                              <Select
                                fullWidth
                                value={get_t_str(day.open_time)}
                                name="open_time"
                                onChange={(evt) =>
                                  handleChange(evt, index, "open_time")
                                }
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "0px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  fontSize: "12px",
                                }}
                              >
                                {generateTimeOptions(maxStartQhnum).map(
                                  (option, index) => (
                                    <MenuItem key={index} value={option}>
                                      {option}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </div>
                            <div>
                              <div className="text-slate-500 flex items-center justify-center">
                                <ArrowForwardIcon
                                  style={{ fontSize: "16px" }}
                                />
                              </div>
                            </div>
                            <div className="w-[100px]">
                              <Select
                                fullWidth
                                value={get_t_str(day.close_time)}
                                name="close_time"
                                onChange={(evt) =>
                                  handleChange(evt, index, "close_time")
                                }
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "0px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  fontSize: "12px",
                                }}
                              >
                                {generateTimeOptions(maxStartQhnum).map(
                                  (option, index) => (
                                    <MenuItem key={index} value={option}>
                                      {option}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
              <div className="mt-6">
                <Divider />
              </div>
              <div className="mt-5">
                <p className="text-slate-900 text-sm font-semibold">
                  Set special hours
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Add special hours of operation for this location (i.e.
                  holidays)
                </p>
              </div>
              {/* <div className="mt-5">
                <Divider />
              </div> */}
              <div className="mt-5">
                {hoursOfOperationData.specialHours.map((day, index) => {
                  return (
                    <div key={index} className="mt-3">
                      <SpecialHoursOfOperationsInput
                        index={index}
                        day={day}
                        hoursOfOperationData={hoursOfOperationData}
                        setHoursOfOperationsData={setHoursOfOperationsData}
                        maxStartQhnum={maxStartQhnum}
                        onDelete={() => handleDeleteSpecialHours(index)}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-3">
                <div
                  className="w-full bg-violet-100 hover:bg-violet-200 text-violet-700 text-sm flex items-center justify-center space-x-1 py-2.5 rounded-lg cursor-pointer"
                  onClick={handleAddSpecialHours}
                >
                  <p>Add special hours</p>
                  <div>
                    <AddIcon style={{ fontSize: "20px" }} />
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Divider />
              </div>
              <div className="mt-5">
                <p className="text-slate-900 text-sm font-semibold">
                  Scheduled recurring changes
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Add weekly hours that begin on an effective date and stay in
                  place until another scheduled change supersedes them
                </p>
              </div>
              {futureRecurringGroups.length > 0 && (
                <div className="mt-5">
                  {futureRecurringGroups.map((group) => (
                    <div
                      key={group.effective_start_date}
                      className="mt-3 first:mt-0"
                    >
                      <FutureRecurringHoursGroupInput
                        group={group}
                        maxStartQhnum={maxStartQhnum}
                        minEffectiveStartDate={formatDate(new Date())}
                        standardOperatingHours={
                          hoursOfOperationData.standardOperatingHours
                        }
                        storeId={hoursOfOperationData.storeId}
                        futureRecurringHours={
                          hoursOfOperationData.futureRecurringHours
                        }
                        blackoutDates={futureRecurringGroups
                          .filter(
                            (otherGroup) =>
                              otherGroup.effective_start_date !==
                              group.effective_start_date
                          )
                          .map((otherGroup) => ({
                            date: otherGroup.effective_start_date,
                          }))}
                        onDelete={() =>
                          handleDeleteFutureRecurringHoursGroup(
                            group.effective_start_date
                          )
                        }
                        onEffectiveStartDateChange={(nextEffectiveStartDate) =>
                          handleFutureRecurringHoursDateChange(
                            group.effective_start_date,
                            nextEffectiveStartDate
                          )
                        }
                        onToggleDay={(dow) =>
                          handleFutureRecurringHoursToggle(
                            group.effective_start_date,
                            dow
                          )
                        }
                        onTimeChange={(dow, field, value) =>
                          handleFutureRecurringHoursChange(
                            group.effective_start_date,
                            dow,
                            field,
                            value
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <div
                  className="w-full bg-violet-100 hover:bg-violet-200 text-violet-700 text-sm flex items-center justify-center space-x-1 py-2.5 rounded-lg cursor-pointer"
                  onClick={handleAddFutureRecurringHoursGroup}
                >
                  <p>Add scheduled recurring change</p>
                  <div>
                    <AddIcon style={{ fontSize: "20px" }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="h-[300px]"></div>
          </div>
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={props.isSavingData}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  // disabled={!checkIfValid()}
                  onClick={() => handleSubmit()}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  Save Changes
                </LoadingButton>
              </div>
              <div className="w-full mt-3">
                <ButtonOutlined text="Cancel" onClick={() => handleClose()} />
              </div>
            </DrawerFooterContainerLarge>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default HoursOfOperationTableRow;
